import { useTranslations } from 'use-intl';
import RichText from '@/components/atoms/rich-text';
import { classnames } from '@/utils/classnames/classnames';
import { AdditionalInfoProps } from '../types';
import SpecificationsTable from './specifications-table';
import DownloadsRow from './downloads-row';

// EWM-198 — Dan's below-fold layout. Three full-width sections in declared
// order: Description (rich text) → Specifications (zebra table) → Downloads
// (outlined pills). Each section hides independently when its data is empty.
// All H2 — single H1 reserved for the product name in the info panel.
//
// EWM-211 — "You May Also Like" used to render here from a mock fixture.
// It now lives in the `related-products-slider` Studio tastic placed below
// the product-details tastic on the PDP route, restyled to Dan's spec.
// EWM-436 — tightened the inter-section gap (py-12 → py-8) so Description /
// Specifications / Downloads sit closer together.
const SECTION_BASE = 'border-t border-gray-200 px-4 py-8 md:px-6 lg:px-8';
const INNER = 'mx-auto max-w-6xl';
const NARROW_INNER = 'mx-auto max-w-[800px]';
const HEADING_BASE = 'text-primary text-22 md:text-24 mb-6 font-bold';

const AdditionalInfo = ({ className, description, specifications, downloads }: AdditionalInfoProps) => {
  const translate = useTranslations();

  const hasDescription = !!description?.trim();
  const hasSpecifications = !!specifications && specifications.length > 0;
  const hasDownloads = !!downloads && downloads.length > 0;

  if (!hasDescription && !hasSpecifications && !hasDownloads) return null;

  return (
    <div className={className}>
      {hasDescription && (
        // EWM-437 — the description copy sits in a gray panel constrained to the
        // same width as the specifications card (NARROW_INNER) instead of a
        // full-bleed gray band, and the heading sits outside the panel to match
        // the Specifications section's heading-above-card layout.
        <section className={SECTION_BASE} aria-labelledby="pdp-section-description">
          <div className={INNER}>
            <div className={NARROW_INNER}>
              <h2 id="pdp-section-description" className={HEADING_BASE}>
                {translate('pdp.section-description')}
              </h2>
              <div className="rounded-lg bg-gray-50 p-6 md:p-8">
                <RichText html={description as string} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* EWM-621 — Specifications and Downloads are joined into ONE white
          section with no dividing border between them (per Dan's style guide);
          Downloads simply sits below Specs with vertical spacing. */}
      {(hasSpecifications || hasDownloads) && (
        <section
          className={classnames(SECTION_BASE, 'bg-white')}
          aria-labelledby={hasSpecifications ? 'pdp-section-specifications' : 'pdp-section-downloads'}
        >
          <div className={INNER}>
            <div className={NARROW_INNER}>
              {hasSpecifications && (
                <>
                  <h2 id="pdp-section-specifications" className={HEADING_BASE}>
                    {translate('pdp.section-specifications')}
                  </h2>
                  <SpecificationsTable specifications={specifications!} />
                </>
              )}
              {hasDownloads && (
                <div className={hasSpecifications ? 'mt-10' : ''}>
                  <h2 id="pdp-section-downloads" className={HEADING_BASE}>
                    {translate('pdp.section-downloads')}
                  </h2>
                  <DownloadsRow downloads={downloads!} />
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default AdditionalInfo;
